import React, { useState, useEffect } from "react";
import SearchBox from "../_common/SearchBox";
import ViewProfileModal from "../_common/ViewProfileModal";
import FeedbackPanel from "../_common/FeedbackPanel";
import ApplicationHistory from "../_common/ApplicationHistory";
import ProgressIndicator from "../_common/ProgressIndicator";
import ScheduleInterviewModal from "../Interview/ScheduleInterviewModal";
import SweetAlert from "react-bootstrap-sweetalert";
import { useEmployerJobApi } from "src/_common/hooks/actions/job/appEmployerJobApiHook";
import { useInterviewApi } from "src/_common/hooks/actions/interview/appInterviewApiHook";
import { useScreeningApi } from "src/_common/hooks/actions/screening/appScreeningApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useJobListSelector } from "src/_common/hooks/selectors/jobSelector.";
import { CANDIDATE_STATUS } from "src/_config";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { hasPermission } from "src/_common/utils/permissionsUtils";
import { useAppUserDetailsSelector, useAppUserPermissionsSelector } from "src/_common/hooks/selectors/userSelector";

interface ScreeningProps {
  jobId?: string;
  onScheduleInterview?: (candidateId: any, currentPage?: number) => void;
  onSendAIInterview?: (candidateId: any, currentPage?: number, aiInterviewData?: any) => void;
  onMoveToInterview?: (candidateId: any, currentPage?: number) => void;
  currentPage?: number;
  onPageChange?: (page: number) => void;
}

interface Candidate {
  id: number;
  jobseekerId: number;
  name: string;
  appliedFor: string;
  match: number;
  matchJustification: string;
  strengths: string[];
  weaknesses: string[];
  topKeywords?: string[];
  screeningId?: string;
  latestScreeningHistory?: {
    status?: number;
    title?: string;
    description?: string;
  };
  originalScreeningData?: any; // Store complete screening data for ViewProfileModal
}

export default function Screening({
  jobId,
  onScheduleInterview,
  onSendAIInterview,
  onMoveToInterview,
  currentPage: propCurrentPage,
  onPageChange,
}: ScreeningProps) {
  const { t } = useTranslation("job-details");
  const userDetails = useAppUserDetailsSelector();
  const userPermissions = useAppUserPermissionsSelector();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(propCurrentPage || 1);

  // Helper function to check permissions for member users
  const canAccessMenu = (menuName: string): boolean => {
    // For members, strictly check permissions - only show if they have access
    if (userDetails?.user_type === "member") {
      return hasPermission(userPermissions, menuName);
    }

    // Non-members (employers/admins) have access to all menus
    return true;
  };

  // Sync currentPage with parent component
  useEffect(() => {
    if (propCurrentPage && propCurrentPage !== currentPage) {
      setCurrentPage(propCurrentPage);
    }
  }, [propCurrentPage]);

  // Notify parent when current page changes
  useEffect(() => {
    if (onPageChange && currentPage !== (propCurrentPage || 1)) {
      onPageChange(currentPage);
    }
  }, [currentPage, onPageChange]);
  const [isViewProfileOpen, setIsViewProfileOpen] = useState(false);
  const [candidatesData, setCandidatesData] = useState<any[]>([]); // Store original screening data
  const [selectedJobseekerId, setSelectedJobseekerId] = useState<number | null>(null);
  const [selectedCandidateDetails, setSelectedCandidateDetails] = useState<any>(null);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [selectedCandidateForFeedback, setSelectedCandidateForFeedback] = useState<Candidate | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [selectedCandidateForHistory, setSelectedCandidateForHistory] = useState<Candidate | null>(null);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [candidateToReject, setCandidateToReject] = useState<Candidate | null>(null);
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [screeningPagination, setScreeningPagination] = useState<any>(null);
  const [fullyScreenedStar, setFullyScreenedStar] = useState<number | null>(null);
  const [verifiedStar, setVerifiedStar] = useState<number | null>(null);
  const [potentialStar, setPotentialStar] = useState<number | null>(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [candidateToSchedule, setCandidateToSchedule] = useState<Candidate | null>(null);
  const [selectedDataType, setSelectedDataType] = useState<string | null>(null); // Track selected filter
  const itemsPerPage = 10;

  // API hooks and selector
  const employerJobApi = useEmployerJobApi();
  const screeningApi = useScreeningApi();
  const interviewApi = useInterviewApi();
  const toast = useToaster();
  const jobListData = useJobListSelector();

  // Find current job from job list
  const currentJob = React.useMemo(() => {
    if (!jobListData || !jobId) return null;
    return jobListData.find((job: any) => job.emp_job_id == jobId);
  }, [jobListData, jobId]);

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    setCurrentPage(1); // Reset to first page when searching
    // The useEffect will trigger fetchScreeningData when searchTerm changes
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1); // Reset to first page when clearing search
  };

  // Handler for stat card clicks
  const handleStatCardClick = (dataType: string | null) => {
    setSelectedDataType(dataType);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    const maxPages = Math.ceil((screeningPagination?.total || 0) / itemsPerPage);

    if (page >= 1 && page <= maxPages) {
      setCurrentPage(page);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const maxPages = Math.ceil((screeningPagination?.total || 0) / itemsPerPage);

    if (currentPage < maxPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Generate page numbers for display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    const maxPages = Math.ceil((screeningPagination?.total || 0) / itemsPerPage);

    if (maxPages <= maxVisiblePages) {
      for (let i = 1; i <= maxPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(maxPages);
      } else if (currentPage >= maxPages - 3) {
        pages.push(1);
        pages.push("...");
        for (let i = maxPages - 4; i <= maxPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(maxPages);
      }
    }

    return pages;
  };
  // Fetch screening data from API
  const fetchScreeningData = () => {
    if (!jobId) {
      console.warn("Job ID is not available");
      return;
    }

    setLoading(true);

    // Prepare additional parameters similar to TalentScouting
    const additionalParams: any = {};
    if (searchTerm && searchTerm.trim()) {
      additionalParams.search = searchTerm.trim();
    }
    additionalParams.status = [CANDIDATE_STATUS.SCREENED];

    // Add data_type filter if a stat card is selected
    if (selectedDataType) {
      additionalParams.data_type = selectedDataType;
    }

    employerJobApi.callGetScreening(
      jobId,
      itemsPerPage,
      (currentPage - 1) * itemsPerPage,
      additionalParams, // Pass search and other params
      (message: string, resp: any) => {
        console.log("Screening data fetched successfully:", resp);

        // Transform and format the screening data
        if (resp && resp.screenings) {
          // Store original screening data for ViewProfileModal
          setCandidatesData(resp.screenings);
          setVerifiedStar(resp.subscribed_count);
          setPotentialStar(resp.potential_match_count);
          const formattedCandidates = resp.screenings.map((screening: any) => {
            // Extract candidate name from the new jobseeker_details structure
            let candidateName = "";

            if (screening.jobseeker_details && screening.jobseeker_details.length > 0) {
              const jobseekerDetails = screening.jobseeker_details[0];

              if (jobseekerDetails.basic_details && jobseekerDetails.basic_details.length > 0) {
                const basicDetails = jobseekerDetails.basic_details[0];

                if (basicDetails.name && basicDetails.name.trim()) {
                  candidateName = basicDetails.name.trim();
                } else if (basicDetails.first_name && basicDetails.last_name) {
                  candidateName = `${basicDetails.first_name} ${basicDetails.last_name}`.trim();
                } else if (basicDetails.first_name) {
                  candidateName = basicDetails.first_name.trim();
                } else {
                  candidateName = t("jobDetails.screening.notAvailable");
                }
              } else {
                candidateName = t("jobDetails.screening.notAvailable");
              }
            } else {
              candidateName = t("jobDetails.screening.notAvailable");
            }

            return {
              id: screening.id,
              jobseekerId: screening.jobseeker_id,
              name: candidateName,
              appliedFor: currentJob?.job_title || "-",
              match: screening.matching_percentage || 0,
              matchJustification: screening.match_justification || "",
              strengths: screening.strengths
                ? Array.isArray(screening.strengths)
                  ? screening.strengths.map((s: string) => s.replace(/^\d+\.\s*/, "").trim()).filter((s: string) => s)
                  : typeof screening.strengths === "string"
                  ? screening.strengths
                      .split("\n")
                      .map((s: string) => s.replace(/^\d+\.\s*/, "").trim())
                      .filter((s: string) => s)
                  : []
                : [],
              weaknesses: screening.weaknesses
                ? Array.isArray(screening.weaknesses)
                  ? screening.weaknesses.map((w: string) => w.replace(/^\d+\.\s*/, "").trim()).filter((w: string) => w)
                  : typeof screening.weaknesses === "string"
                  ? screening.weaknesses
                      .split("\n")
                      .map((w: string) => w.replace(/^\d+\.\s*/, "").trim())
                      .filter((w: string) => w)
                  : []
                : [],
              topKeywords: screening.top_keywords
                ? Array.isArray(screening.top_keywords)
                  ? screening.top_keywords
                  : []
                : [],
              screeningId: screening.id,
              latestScreeningHistory: screening.latest_screening_history || null,
              // Store complete screening data for ViewProfileModal
              originalScreeningData: screening,
            };
          });

          setCandidates(formattedCandidates);
        } else {
          setCandidates([]);
        }

        if (resp && resp.pagination) {
          setScreeningPagination(resp.pagination);
        }

        setLoading(false);
      },
      (error: string) => {
        console.error("Error fetching screening data:", error);
        // toast.error(error);
        setLoading(false);
      }
    );
  };

  // useEffect to call API on component mount and when dependencies change
  useEffect(() => {
    if (jobId) {
      fetchScreeningData();
    }
  }, [currentPage, jobId, searchTerm, selectedDataType]); // Added searchTerm and selectedDataType to dependencies

  const getMatchColor = (match: number) => {
    if (match >= 80) return "#22C55E";
    if (match >= 60) return "#F59E0B";
    return "#EF4444";
  };

  const handleSendAIInterview = (candidateId: any) => {
    const candidate = candidates.find((c) => c.id === candidateId);
    if (!candidate) return;

    // Prepare AI interview data
    const aiInterviewData = {
      candidate,
      screeningId: candidate.screeningId,
    };

    // Call parent callback to open SetValidityModal
    // The actual AI interview logic will be executed after validity is set
    if (onSendAIInterview) {
      onSendAIInterview(candidateId, currentPage, aiInterviewData);
    }
  };

  const handleScheduleInterview = (candidateId: any) => {
    console.log("Schedule Interview for candidate:", candidateId);
    const candidate = candidates.find((c) => c.id === candidateId);
    console.log("Found candidate:", candidate);
    // Commented: switching to Interview tab via parent callback.
    // if (onScheduleInterview) {
    //   onScheduleInterview(candidateId, currentPage);
    // }
    if (candidate) {
      setCandidateToSchedule(candidate);
      setIsScheduleModalOpen(true);
    }
  };

  const handleViewStatus = (candidateId: any) => {
    const candidate = candidates.find((c) => c.id === candidateId);
    if (candidate && candidate.screeningId) {
      // Open the ApplicationHistory panel - it will handle the API call internally
      setSelectedCandidateForHistory(candidate);
      setIsHistoryOpen(true);
    } else {
      console.warn("No screening ID found for candidate:", candidateId);
      toast.error("Unable to fetch history: No screening ID found");
    }
  };

  const handleAddNote = (candidateId: any) => {
    const candidate = candidates.find((c) => c.id === candidateId);
    if (candidate) {
      setSelectedCandidateForFeedback(candidate);
      setIsFeedbackOpen(true);
    }
  };

  const handleReject = (candidateId: any) => {
    const candidate = candidates.find((c) => c.id === candidateId);
    if (candidate) {
      setCandidateToReject(candidate);
      setShowRejectConfirm(true);
    }
  };

  const handleConfirmReject = () => {
    if (candidateToReject) {
      // Call addhistory API with rejection parameters
      const payload = {
        // ...(userDetails?.user_type != "member" ? { member_id: userDetails?.user?.id } : {}),
        screening_id: candidateToReject.screeningId,
        application_type: 1,
        status: 2,
        title: t("jobDetails.screening.apiPayload.rejectTitle"),
        description: t("jobDetails.screening.apiPayload.rejectDescription"),
      };

      screeningApi.callAddFeedback(
        payload,
        (message: string, resp: any) => {
          console.log("Candidate rejection recorded successfully:", resp);
          toast.success(t("jobDetails.screening.rejectSuccess"));

          // Optionally refresh the screening data
          fetchScreeningData();
        },
        (error: string) => {
          console.error("Error recording candidate rejection:", error);
          toast.error(t("jobDetails.screening.rejectError") + ": " + error);
        }
      );

      // Also update candidate status to REJECTED
      screeningApi.callUpdateStatus(
        {
          screening_id: candidateToReject?.screeningId,
          status: CANDIDATE_STATUS.REJECTED,
        },
        (message: string, resp: any) => {
          // status updated
        },
        (error: string) => {
          // silent fail to not block UX
        }
      );

      // Close the modal
      setShowRejectConfirm(false);
      setCandidateToReject(null);
    }
  };

  const handleCancelReject = () => {
    setShowRejectConfirm(false);
    setCandidateToReject(null);
  };

  const handleViewProfile = (candidate: Candidate) => {
    // Use the originalScreeningData if available, otherwise fallback to candidatesData lookup
    const originalScreeningData =
      candidate.originalScreeningData || candidatesData.find((screening) => screening.id === candidate.id);

    if (originalScreeningData && originalScreeningData.jobseeker_details) {
      setSelectedJobseekerId(candidate.jobseekerId);

      const jobseekerDetails = originalScreeningData.jobseeker_details[0];
      const basicDetails = jobseekerDetails.basic_details?.[0] || {};

      // Map the data structure to match ViewProfileModal expectations
      const detailsToSet = {
        name: basicDetails.name || candidate.name,
        avatar: originalScreeningData.document_full_path?.profile_pic || null,
        currentDesignation: basicDetails.current_designation || "",
        currentLocation: basicDetails.current_location || "",
        totalExperienceYear: basicDetails.total_experience_year || "",
        gender: basicDetails.gender_id ? (basicDetails.gender_id === 1 ? "Male" : "Female") : "-",
        currentSalary: basicDetails.current_salary || "",
        currentSalaryCurrency: basicDetails.current_salary_currency || "",
        skills: Array.isArray(basicDetails.key_skills) ? basicDetails.key_skills.join(", ") : "",
        profileSummary: basicDetails.profile_summary || "",
        // Map basic details for file downloads
        basicDetails: {
          resume_file: originalScreeningData.document_full_path?.system_resume_file || null,
          video_file: originalScreeningData.document_full_path?.video_file || null,
          jobseeker_id: candidate.jobseekerId,
        },
        // Map employment details
        employmentDetails: jobseekerDetails.experience || [],
        // Map education details
        educationDetails: jobseekerDetails.education || [],
        // Map course details
        courseDetails: jobseekerDetails.courses || [],
        // Map project details
        projectDetails: jobseekerDetails.projects || [],
        // Map certificate details
        certificateDetails: jobseekerDetails.certifications || [],
        // Public profile information
        jobseeker_public_profiles: jobseekerDetails.jobseeker_public_profiles || [],
      };
      setSelectedCandidateDetails(detailsToSet);
    } else {
      // Fallback - just pass jobseekerId for API fetch
      setSelectedJobseekerId(candidate.jobseekerId);
      setSelectedCandidateDetails(null);
    }
    setIsViewProfileOpen(true);
  };

  const handleCloseFeedback = () => {
    setIsFeedbackOpen(false);
    setSelectedCandidateForFeedback(null);
  };

  const handleCloseHistory = () => {
    setIsHistoryOpen(false);
    setSelectedCandidateForHistory(null);
  };

  const handleCloseScheduleModal = () => {
    setIsScheduleModalOpen(false);
    setCandidateToSchedule(null);
  };

  const handleScheduleConfirm = (scheduleData: any) => {
    console.log("Interview scheduled with data:", scheduleData);
    console.log("For candidate:", candidateToSchedule);
    // Show success and close modal; refresh list to reflect any updates if needed
    // toast.success(t("jobDetails.screening.scheduleSuccess"));

    // Add feedback for interview scheduled
    const feedbackPayload = {
      // ...(userDetails?.user_type != "member" ? { member_id: userDetails?.user?.id } : {}),
      screening_id: candidateToSchedule?.screeningId,
      application_type: 1,
      status: 9,
      title: t("jobDetails.screening.apiPayload.scheduleInterviewTitle") || "Interview Scheduled",
      description:
        t("jobDetails.screening.apiPayload.scheduleInterviewDescription") ||
        "Interview has been scheduled with the candidate.",
    };

    screeningApi.callAddFeedback(
      feedbackPayload,
      (message: string, resp: any) => {
        console.log("Interview schedule feedback recorded successfully:", resp);

        // Update candidate status to Scheduled Interview
        screeningApi.callUpdateStatus(
          {
            screening_id: candidateToSchedule?.screeningId,
            status: CANDIDATE_STATUS.SCHEDULE_INTERVIEW,
          },
          (message: string, resp: any) => {
            console.log("Candidate status updated to Scheduled Interview:", resp);
            handleCloseScheduleModal();
            // Optionally refresh screening data
            // fetchScreeningData();
            // Navigate to Interview tab after successful scheduling
            if (onMoveToInterview && candidateToSchedule?.screeningId) {
              onMoveToInterview(candidateToSchedule.screeningId, currentPage);
            }
          },
          (error: string) => {
            toast.error(t("jobDetails.screening.statusUpdateError") + ": " + error);
          }
        );
      },
      (error: string) => {
        console.error("Error recording interview schedule feedback:", error);
        // Continue with the flow even if feedback fails
        handleCloseScheduleModal();
      }
    );
  };

  useEffect(() => {
    if (screeningPagination && !selectedDataType) {
      setFullyScreenedStar(screeningPagination?.total || 0);
    }
  }, [screeningPagination, selectedDataType]);

  return (
    <div className="screening-wrapper">
      {/* Header Section */}
      <div className="screening-header">
        <div className="header-left-ai-job">
          <div className="job-overview-section">
            <h1 className="job-overview-title">{currentJob?.job_title}</h1>
            <p className="job-overview-subtitle">{t("jobDetails.screening.subtitle")}</p>
          </div>
        </div>
        {/* Only show search when there are candidates or search is active or loading */}
        {!(!loading && (!candidates || candidates.length === 0) && !selectedDataType) && (
          <div className="header-search">
            <SearchBox
              placeholder={t("jobDetails.screening.searchPlaceholder")}
              onSearch={handleSearch}
              onClear={handleClearSearch}
            />
          </div>
        )}
      </div>
      {/* Conditional Content: Show progress card if no screening data, otherwise show stats and candidates */}
      {!loading && (!candidates || candidates.length === 0) && !selectedDataType ? (
        <ProgressIndicator stage="screening" />
      ) : (
        <>
          {/* Stats Cards */}
          <div className="screening-stats-grid">
            <div
              className={`screening-stat-card screening-stat-red ${selectedDataType === null ? "active" : ""}`}
              onClick={() => handleStatCardClick(null)}
              style={{ cursor: "pointer" }}
            >
              <div className="screening-stat-content">
                <h3 className="screening-stat-title">{t("jobDetails.screening.fullyScreened")}</h3>
                <span className="screening-stat-number">{fullyScreenedStar || 0}</span>
              </div>
              <div className="screening-stat-icon screening-stat-icon-red">
                <i className="fa fa-file-text"></i>
              </div>
            </div>
            <div
              className={`screening-stat-card screening-stat-blue ${
                selectedDataType === "potential_match" ? "active" : ""
              }`}
              onClick={() => handleStatCardClick("potential_match")}
              style={{ cursor: "pointer" }}
            >
              <div className="screening-stat-content">
                <h3 className="screening-stat-title">{t("jobDetails.screening.potentialStar")}</h3>
                <span className="screening-stat-number">{potentialStar || 0}</span>
              </div>
              <div className="screening-stat-icon screening-stat-icon-blue">
                <i className="fa fa-file-text"></i>
              </div>
            </div>
            <div
              className={`screening-stat-card screening-stat-green ${
                selectedDataType === "subscribed" ? "active" : ""
              }`}
              onClick={() => handleStatCardClick("subscribed")}
              style={{ cursor: "pointer" }}
            >
              <div className="screening-stat-content">
                <h3 className="screening-stat-title">{t("jobDetails.screening.verifiedStar")}</h3>
                <span className="screening-stat-number">{verifiedStar || 0}</span>
              </div>
              <div className="screening-stat-icon screening-stat-icon-green">
                <i className="fa fa-check-square"></i>
              </div>
            </div>
          </div>

          {/* Candidates List */}
          <div className="screening-candidates-section">
            {loading ? (
              <div className="screening-loading" style={{ textAlign: "center", padding: "60px 20px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "15px",
                    marginBottom: "10px",
                  }}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      border: "3px solid #f3f3f3",
                      borderTop: "3px solid #3498db",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  ></div>
                  <span style={{ fontSize: "16px", color: "#666" }}>{t("jobDetails.screening.loadingData")}</span>
                </div>
                <p style={{ color: "#999", fontSize: "14px", margin: "0" }}>{t("jobDetails.screening.pleaseWait")}</p>
              </div>
            ) : candidates?.length > 0 ? (
              candidates.map((candidate) => (
                <div key={candidate.id} className="screening-candidate-card">
                  <div className="screening-candidate-header">
                    <div className="screening-candidate-info">
                      <h3
                        className="screening-candidate-name"
                        onClick={() => handleViewProfile(candidate)}
                        style={{ cursor: "pointer", color: "#3b82f6" }}
                      >
                        {candidate.name}
                      </h3>
                      <p className="screening-candidate-position">
                        {t("jobDetails.screening.appliedFor")} {candidate.appliedFor}
                      </p>
                      {candidate.topKeywords && candidate.topKeywords.length > 0 && (
                        <div className="screening-top-keywords" style={{ marginTop: "20px" }}>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                            {candidate.topKeywords.map((keyword, index) => (
                              <span
                                key={index}
                                style={{
                                  display: "inline-block",
                                  padding: "4px 10px",
                                  backgroundColor: "#eff6ff",
                                  color: "#1e40af",
                                  borderRadius: "12px",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  border: "1px solid #bfdbfe",
                                }}
                              >
                                {keyword}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="screening-match-score">
                      <div className="screening-match-circle">
                        <svg width="70" height="70" viewBox="0 0 70 70">
                          <circle cx="35" cy="35" r="30" fill="none" stroke="#e5e7eb" strokeWidth="6" />
                          <circle
                            cx="35"
                            cy="35"
                            r="30"
                            fill="none"
                            stroke={getMatchColor(candidate.match)}
                            strokeWidth="6"
                            strokeDasharray={`${candidate.match * 1.88} 188.5`}
                            strokeLinecap="round"
                            transform="rotate(-90 35 35)"
                          />
                        </svg>
                        <div className="screening-match-text">
                          <span
                            className="screening-match-percentage"
                            style={{ color: getMatchColor(candidate.match) }}
                          >
                            {Math.round(candidate.match)}%
                          </span>
                          <small className="screening-match-label d-none">Match</small>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="screening-candidate-content">
                    {candidate?.matchJustification && (
                      <div className="screening-match-justification">
                        <h4 className="screening-section-title">{t("jobDetails.screening.matchJustification")}</h4>
                        <p className="screening-justification-text">{candidate.matchJustification}</p>
                      </div>
                    )}

                    <div className="screening-strengths-weaknesses">
                      {candidate?.strengths && candidate.strengths.length > 0 && (
                        <div className="screening-strengths">
                          <h4 className="screening-section-title screening-strengths-title">
                            <i className="fa fa-circle screening-strengths-icon"></i>
                            {t("jobDetails.screening.strengths")}
                          </h4>
                          <ul className="screening-list">
                            {candidate.strengths.map((strength, index) => (
                              <li key={index} className="screening-list-item">
                                <i className="fa fa-star screening-strength-star"></i>
                                {strength}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {candidate?.weaknesses && candidate.weaknesses.length > 0 && (
                        <div className="screening-weaknesses">
                          <h4 className="screening-section-title screening-weaknesses-title">
                            <i className="fa fa-circle screening-weaknesses-icon"></i>
                            {t("jobDetails.screening.weaknesses")}
                          </h4>
                          <ul className="screening-list">
                            {candidate.weaknesses.map((weakness, index) => (
                              <li key={index} className="screening-list-item">
                                <i className="fa fa-flag screening-weakness-icon"></i>
                                {weakness}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="screening-candidate-actions">
                    <div className="screening-action-buttons-left">
                      <button
                        className="screening-btn screening-btn-view-status"
                        onClick={() => handleViewStatus(candidate.id)}
                      >
                        {t("jobDetails.screening.viewStatus")}
                      </button>
                      <button
                        className="screening-btn screening-btn-add-note"
                        onClick={() => handleAddNote(candidate.id)}
                      >
                        <i className="fa fa-edit"></i>
                        {t("jobDetails.screening.addNote")}
                      </button>
                      {/* Hide reject button if candidate is already rejected (status = 2) */}
                      {candidate.latestScreeningHistory?.status == 2 ? (
                        <button className="screening-btn screening-btn-rejected" disabled>
                          <i className="fa fa-times-circle"></i>
                          {t("jobDetails.screening.rejected")}
                        </button>
                      ) : candidate.latestScreeningHistory?.status == 3 ? (
                        <button className="screening-btn screening-btn-hired" disabled>
                          <i className="fa fa-check-circle"></i>
                          {t("jobDetails.screening.hired")}
                        </button>
                      ) : (
                        <button
                          className="screening-btn screening-btn-reject"
                          onClick={() => handleReject(candidate.id)}
                        >
                          <i className="fa fa-times"></i>
                          {t("jobDetails.screening.reject")}
                        </button>
                      )}
                    </div>

                    <div className="screening-action-buttons-right">
                      {canAccessMenu("create_ai_interview") && (
                        <button
                          className="screening-btn screening-btn-send-ai"
                          onClick={() => handleSendAIInterview(candidate.id)}
                        >
                          <i className="fa fa-paper-plane"></i>
                          {t("jobDetails.screening.sendAIInterview")}
                        </button>
                      )}
                      <button
                        className="screening-btn screening-btn-schedule"
                        onClick={() => handleScheduleInterview(candidate.id)}
                      >
                        <i className="fa fa-calendar"></i>
                        {t("jobDetails.screening.scheduleInterview")}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="screening-no-candidates">
                <p>{t("jobDetails.screening.noCandidates")}</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {candidates.length > 0 && Math.ceil((screeningPagination?.total || 0) / itemsPerPage) > 1 ? (
            <div className="pagination">
              <button className="page-btn" onClick={handlePrevPage} disabled={currentPage === 1}>
                {t("jobDetails.screening.prev")}
              </button>
              <div className="page-numbers">
                {getPageNumbers().map((page, index) =>
                  page === "..." ? (
                    <span key={`ellipsis-${index}`}>...</span>
                  ) : (
                    <button
                      key={page}
                      className={`page-num ${currentPage === page ? "active" : ""}`}
                      onClick={() => handlePageChange(page as number)}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>
              <button
                className="page-btn"
                onClick={handleNextPage}
                disabled={currentPage === Math.ceil((screeningPagination?.total || 0) / itemsPerPage)}
              >
                {t("jobDetails.screening.next")}
              </button>
            </div>
          ) : null}
        </>
      )}
      {/* View Profile Modal */}
      <ViewProfileModal
        isOpen={isViewProfileOpen}
        onClose={() => setIsViewProfileOpen(false)}
        jobId={jobId}
        jobseekerId={selectedJobseekerId || undefined}
        candidateDetails={selectedCandidateDetails}
      />
      {/* Feedback Panel */}
      <FeedbackPanel
        candidateName={selectedCandidateForFeedback?.name || ""}
        screeningId={selectedCandidateForFeedback?.screeningId || ""}
        isOpen={isFeedbackOpen}
        onClose={handleCloseFeedback}
      />
      {/* Application History Panel */}
      <ApplicationHistory
        candidateName={selectedCandidateForHistory?.name || ""}
        screeningId={selectedCandidateForHistory?.screeningId}
        isOpen={isHistoryOpen}
        onClose={handleCloseHistory}
      />
      {/* Reject Confirmation Modal */}
      {showRejectConfirm && candidateToReject && (
        <SweetAlert
          warning
          showCancel
          confirmBtnText={t("jobDetails.screening.confirmReject.yesReject")}
          confirmBtnBsStyle="danger"
          cancelBtnText={t("jobDetails.screening.confirmReject.cancel")}
          cancelBtnBsStyle="default"
          title={t("jobDetails.screening.confirmReject.title")}
          onConfirm={handleConfirmReject}
          onCancel={handleCancelReject}
        >
          {t("jobDetails.screening.confirmReject.message")} <strong>{candidateToReject.name}</strong>?
          <br />
          <br />
          {t("jobDetails.screening.confirmReject.warning")}
        </SweetAlert>
      )}
      {/* Schedule Interview Modal */}
      <ScheduleInterviewModal
        isOpen={isScheduleModalOpen}
        onClose={handleCloseScheduleModal}
        candidateName={candidateToSchedule?.name || ""}
        candidateId={(candidateToSchedule?.screeningId || candidateToSchedule?.id || "") as any}
        jobId={jobId || ""}
        onSchedule={handleScheduleConfirm}
      />
    </div>
  );
}
